/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import React, { useEffect, useRef } from 'react';
import { Animated } from 'react-native';

export enum ScreenType {
  LOGIN = 'LOGIN',
  FORGOT_PASSWORD = 'FORGOT_PASSWORD',
  SIGNUP = 'SIGNUP',
  VERIFY_ACCOUNT = 'VERIFY_ACCOUNT',
  VERIFY_OTP = 'VERIFY_OTP',
  VERIFY_EMAIL_OTP = 'VERIFY_EMAIL_OTP',
  VERIFY_EMAIL = 'VERIFY_EMAIL',
  RESET_PASSWORD = 'RESET_PASSWORD',
  TWO_FACTOR_AUTH = 'TWO_FACTOR_AUTH',
}

export enum TransitionType {
  FADE_SLIDE = 'FADE_SLIDE',
  SLIDE_LEFT = 'SLIDE_LEFT',
  SLIDE_RIGHT = 'SLIDE_RIGHT',
  SCALE = 'SCALE',
}

export type ScreenTransitionProps = {
  children: React.ReactNode;
  isVisible: boolean;
  animationDuration?: number;
  transitionType?: TransitionType;
};

const ScreenTransition: React.FC<ScreenTransitionProps> = ({
  children,
  isVisible,
  animationDuration = 300,
  transitionType = TransitionType.FADE_SLIDE,
}) => {
  const fadeAnim = useRef(new Animated.Value(isVisible ? 1 : 0)).current;
  const slideAnim = useRef(new Animated.Value(isVisible ? 0 : 50)).current;
  const scaleAnim = useRef(new Animated.Value(isVisible ? 1 : 0.95)).current;
  const slideLeftAnim = useRef(
    new Animated.Value(isVisible ? 0 : -300),
  ).current;
  const slideRightAnim = useRef(
    new Animated.Value(isVisible ? 0 : 300),
  ).current;

  useEffect(() => {
    const animations: Animated.CompositeAnimation[] = [];

    // Always include fade animation
    animations.push(
      Animated.timing(fadeAnim, {
        toValue: isVisible ? 1 : 0,
        duration: animationDuration,
        useNativeDriver: true,
      }),
    );

    // Add specific animations based on transition type
    switch (transitionType) {
      case TransitionType.FADE_SLIDE:
        animations.push(
          Animated.timing(slideAnim, {
            toValue: isVisible ? 0 : 50,
            duration: animationDuration,
            useNativeDriver: true,
          }),
        );
        break;
      case TransitionType.SLIDE_LEFT:
        animations.push(
          Animated.timing(slideLeftAnim, {
            toValue: isVisible ? 0 : -300,
            duration: animationDuration,
            useNativeDriver: true,
          }),
        );
        break;
      case TransitionType.SLIDE_RIGHT:
        animations.push(
          Animated.timing(slideRightAnim, {
            toValue: isVisible ? 0 : 300,
            duration: animationDuration,
            useNativeDriver: true,
          }),
        );
        break;
      case TransitionType.SCALE:
        animations.push(
          Animated.timing(scaleAnim, {
            toValue: isVisible ? 1 : 0.95,
            duration: animationDuration,
            useNativeDriver: true,
          }),
        );
        break;
    }

    Animated.parallel(animations).start();
  }, [
    isVisible,
    fadeAnim,
    slideAnim,
    scaleAnim,
    slideLeftAnim,
    slideRightAnim,
    animationDuration,
    transitionType,
  ]);

  if (!isVisible) {
    return null;
  }

  const getTransformStyle = () => {
    const transforms: any[] = [];

    switch (transitionType) {
      case TransitionType.FADE_SLIDE:
        transforms.push({ translateY: slideAnim });
        break;
      case TransitionType.SLIDE_LEFT:
        transforms.push({ translateX: slideLeftAnim });
        break;
      case TransitionType.SLIDE_RIGHT:
        transforms.push({ translateX: slideRightAnim });
        break;
      case TransitionType.SCALE:
        transforms.push({ scale: scaleAnim });
        break;
    }

    return transforms;
  };

  return (
    <Animated.View
      style={{
        opacity: fadeAnim,
        transform: getTransformStyle(),
      }}
    >
      {children}
    </Animated.View>
  );
};

export default ScreenTransition;
